import { useMemo, useState } from 'react';
import { parseISO, format } from 'date-fns';
import { ka } from 'date-fns/locale';
import { ChevronDownIcon } from 'lucide-react';

import { useTranslations } from '@/i18n';
import { Button } from '@/components/ui/button';
import type { TripApprovalHistory } from '@/hooks/trips/types';
import { cn } from '@/lib/utils';

interface TripActivityCardProps {
  approvalHistory?: TripApprovalHistory[];
  defaultOpen?: boolean;
}

export default function TripActivityCard({
  approvalHistory = [],
  defaultOpen = true,
}: TripActivityCardProps) {
  const t = useTranslations('trips.activity');
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const { activities, lastUpdate } = useMemo(() => {
    const parsed = approvalHistory
      .filter((entry): entry is TripApprovalHistory => entry != null)
      .map((entry) => {
        const parsedDate =
          entry.actionDate && !Number.isNaN(Date.parse(entry.actionDate))
            ? parseISO(entry.actionDate)
            : null;
        const timestamp = parsedDate ? parsedDate.getTime() : 0;

        return {
          id:
            entry.id != null && entry.id !== 0
              ? String(entry.id)
              : `${entry.actionType}-${timestamp}`,
          actionType: entry.actionType,
          summary: entry.actionSummary?.trim().length ? entry.actionSummary : entry.actionType,
          dateLabel: formatActivityDate(parsedDate),
          sortKey: timestamp,
          user: entry.actionUserName || '—',
          userPosition: entry.actionUserPosition,
          comment: entry.comments?.trim().length ? entry.comments : undefined,
        };
      })
      .sort((a, b) => b.sortKey - a.sortKey);

    const latest = parsed[0];
    return {
      activities: parsed,
      lastUpdate: latest ? latest.dateLabel : '—',
    };
  }, [approvalHistory]);

  if (!activities.length) {
    return null;
  }

  return (
    <div className="flex flex-col rounded-[24px] bg-white shadow-[0px_8px_24px_-8px_rgba(31,31,31,0.08)]">
      <div className="flex items-center justify-between rounded-t-[24px] bg-white px-5 py-4">
        <h2 className="text-[16px] leading-6 font-bold text-[#292929]">{t('title')}</h2>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-[14px] leading-5">
            <span className="text-[#666666]">{t('lastUpdate')}:</span>
            <span className="font-medium text-[#292929]">{lastUpdate}</span>
          </div>
          <Button
            type="button"
            variant="ghost"
            onClick={() => setIsOpen((prev) => !prev)}
            className="size-9 shrink-0 rounded-full bg-[#f9f9f9] p-0 text-[#292929] hover:bg-[#f1f1f1]"
            aria-label={isOpen ? t('collapse') : t('expand')}
          >
            <ChevronDownIcon
              className={cn('size-4 transition-transform duration-200', !isOpen && 'rotate-180')}
            />
          </Button>
        </div>
      </div>

      {isOpen ? (
        <div className="flex flex-col gap-4 px-5 pt-3 pb-4">
          {activities.map((activity, index) => {
            const style = getTimelineStyle(activity.actionType);
            const showConnector = index < activities.length - 1;

            return (
              <div key={activity.id} className="flex gap-3">
                <div className="relative flex w-3 shrink-0 justify-center">
                  <span
                    className={cn(
                      'mt-1 h-2.5 w-2.5 rounded-full border border-white shadow-[0_0_0_1px_rgba(0,0,0,0.04)]',
                      style.dot
                    )}
                  />
                  {showConnector && (
                    <span className="absolute top-4 left-1/2 h-[calc(100%-1rem)] -translate-x-1/2 border-l border-dashed border-[#e6e6e6]" />
                  )}
                </div>

                <div className="flex flex-1 flex-col gap-3 rounded-[18px] bg-white px-0.5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex flex-col">
                      <span className="text-[14px] leading-5 font-medium text-[#292929]">
                        {activity.summary}
                      </span>
                      <span className="text-[15px] leading-6 text-[#666666]">
                        {activity.dateLabel}
                      </span>
                    </div>
                    <div className="flex flex-col items-end text-right">
                      <span className="text-[14px] leading-5 text-[#292929]">{activity.user}</span>
                      {activity.userPosition ? (
                        <span className="text-[15px] leading-6 text-[#666666]">
                          {activity.userPosition}
                        </span>
                      ) : null}
                    </div>
                  </div>

                  {activity.comment ? (
                    <div className="rounded-[12px] border border-[#f1f1f1] bg-[#f9f9f9] px-3 py-2 text-[15px] leading-6 text-[#666666]">
                      {activity.comment}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

type TimelineStyle = {
  dot: string;
};

const ACTION_TYPE_STYLES: Record<string, TimelineStyle> = {
  approved: { dot: 'bg-[#30a46c]' },
  submitted: { dot: 'bg-[#2e7eff]' },
  declined: { dot: 'bg-[#e5484d]' },
  returned_back: { dot: 'bg-[#f7b731]' },
  resubmitted: { dot: 'bg-[#ed8a5c]' },
  sent_to_accountant: { dot: 'bg-[#6e56cf]' },
  cancelled: { dot: 'bg-[#e6e6e6]' },
  default: { dot: 'bg-[#e6e6e6]' },
};

function getTimelineStyle(actionType: string | undefined): TimelineStyle {
  if (!actionType) {
    return ACTION_TYPE_STYLES.default;
  }

  return ACTION_TYPE_STYLES[actionType] ?? ACTION_TYPE_STYLES.default;
}

function formatActivityDate(date: Date | null): string {
  if (!date) {
    return '—';
  }

  const formatted = format(date, 'd MMM, yyyy', { locale: ka });
  return formatted.replace('.,', ',');
}
